"use client";

import { useQuery } from "@tanstack/react-query";
import type { MovementState } from "@/lib/keyword-explorer";
import type { ApplePopularity, KeywordDifficulty, KeywordHints } from "@/lib/keywords/keyword-scores";

export type KeywordRank = {
  keyword_id: string;
  keyword: string;
  country_code: string;
  rank_position: number;
  /** date-only string, the day the search page was collected */
  observed_on: string;
  /** first - latest, so a climb up the page is positive. Null with nothing to compare. */
  movement: number | null;
  movement_state: MovementState;
  /** Where this app stood on the sweep before the latest one, and when that was. */
  previous_rank: number | null;
  previous_on: string | null;
  /**
   * OUR difficulty estimate for this keyword on its newest scored day. Null
   * when the keyword has never been scored -- which is not a zero, and the
   * column renders a dash for it.
   */
  difficulty: (KeywordDifficulty & { observed_on: string }) | null;
  /**
   * APPLE'S own search popularity for this term. Null when the term is outside
   * the top 500 of its genre, which is Apple's whole board, or when no pull has
   * run at all. `apple_pulled` below separates those two.
   */
  apple: ApplePopularity | null;
  /**
   * OUR popularity estimate, read from Apple's search-suggestion order. Null
   * when the harvest has not reached this keyword; a row whose score is null
   * means Apple never offers the term, which is a different dash.
   */
  hints: KeywordHints | null;
  /**
   * This app's rank on every sweep day in the window asked for, oldest first,
   * with a null for a day the keyword was swept and the app was not in it. Two
   * days by default, which is the movement comparison and nothing more.
   */
  points: { observed_on: string; rank: number | null }[];
};

export type KeywordRanksResult = {
  items: KeywordRank[];
  total: number;
  /** Every sweep day behind the movement column, oldest first. */
  observed_days: string[];
  /**
   * Whether an Apple pull has ever run for this storefront. Without it a blank
   * Apple column reads as "Apple says this term is unpopular", which is the one
   * thing an absent row never means.
   */
  /** Distinct keywords tracked for this app, ranked or not. */
  tracked_total: number;
  apple_pulled: boolean;
  /**
   * True when the route could not read the keyword tables at all. It is what
   * separates a broken deployment from the honest empty answer almost every app
   * gives, and the surfaces draw different things for the two.
   */
  unavailable?: boolean;
};

const UNAVAILABLE: KeywordRanksResult = {
  items: [], total: 0, tracked_total: 0, observed_days: [], apple_pulled: false, unavailable: true,
};

/**
 * One app's search positions, over `days` sweep days.
 *
 * `days` is part of the query key, so every caller asking for the same window
 * shares one fetch: the Search positions card and the Tracked keywords table sit
 * on one page and both ask for 14. Two windows would be two requests for the
 * same rows, which is the defect this argument exists to avoid.
 */
export function useKeywordRanks(appId: number, days: number) {
  const { data, isLoading, isError, refetch } = useQuery<KeywordRanksResult, Error>({
    queryKey: ["keyword-ranks", appId, days],
    enabled: !!appId,
    // The collector writes at most one new day per day.
    staleTime: 6 * 60 * 60 * 1000,
    queryFn: async () => {
      const res = await fetch(`/api/app-detail/keywords?appId=${appId}&days=${days}`);
      // A non-200 is the same news as the route's own `unavailable`: the panel
      // can say so rather than drawing an empty table it cannot explain.
      if (!res.ok) return UNAVAILABLE;
      return (await res.json()) as KeywordRanksResult;
    },
  });
  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    trackedTotal: data?.tracked_total ?? 0,
    observedDays: data?.observed_days ?? [],
    applePulled: data?.apple_pulled ?? false,
    unavailable: data?.unavailable === true || isError,
    isLoading,
    // The error box draws a Retry, so it needs something to call.
    refetch,
  };
}
